/**
 * useProjectToEnvironment — TanStack mutation hook for projecting an
 * investigation onto the Environment canvas (bug-20260410-46b2e0).
 *
 * Wraps POST /api/v1/investigations/{id}/project-to-environment.
 *
 * On success, invalidates two REAL consumer keys (grep-verified against
 * the codebase — see sprint-bug-7 engineer-feedback.md BUG-1):
 *
 *   - ['investigations']             — useInvestigation.ts:55
 *     Source of truth for the Workspace investigation list. The workbench
 *     reads selectedInvestigation via investigations.find(...) against
 *     this list, so invalidating it refreshes projected_to_environment_at
 *     and flips the resolver button label/tone.
 *
 *   - ['environment-graph-d3']       — EnvironmentGraphPage.tsx:632
 *     Page-local useEnvironmentGraph query for the D3 knowledge graph.
 *     Broad invalidation (no theatreId segment) so every open graph
 *     instance refetches. The D3 graph has staleTime 30_000 and NO
 *     refetchInterval, so manual invalidation here is the ONLY path
 *     to refresh the graph after projection — without this, the new
 *     investigation node never appears until a full page reload.
 *
 * Not invalidated:
 *   - ['environment-graph', ...] (useGraphData.ts:110) — the useGraphData
 *     hook has zero runtime consumers (type-only imports elsewhere), so
 *     it's dead code and invalidating it would be a no-op waste.
 *
 * No toast: the main app surface does not yet use a toast primitive
 * (`useInvestigation.ts` sibling mutations rely on query invalidation
 * alone). Keep this hook convention-compliant; a toast can be layered
 * on later by the callsite if/when a shared toast primitive lands.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  ensureCompositionAndProject,
  type InvestigationProjectionResponse,
} from '../api/investigation';

export interface ProjectToEnvironmentInput {
  investigationId: string;
  /** Target composition. Falsy → GAP-2 fallback (sprint-236): create a blank
   * composition via POST /api/v1/environment/compositions {preset: null},
   * then project into the new id (instead of the backend 400). */
  theatreId?: string | null;
}

export function useProjectToEnvironment() {
  const queryClient = useQueryClient();

  return useMutation<InvestigationProjectionResponse, Error, ProjectToEnvironmentInput>({
    mutationFn: ({ investigationId, theatreId }) =>
      ensureCompositionAndProject(investigationId, theatreId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigations'] });
      queryClient.invalidateQueries({ queryKey: ['environment-graph-d3'] });
      // GAP-2 fallback may have CREATED a composition — refresh the
      // composition-picker list (['theatres', 'list', ...]) so the new
      // composition is discoverable without a reload.
      queryClient.invalidateQueries({ queryKey: ['theatres', 'list'] });
    },
  });
}
